import { Avatar, Box, Button, Stack, Typography } from '@mui/material'
import { ReactNode, useState } from 'react'
import { Icons } from 'utoniq-ui'

import {
  ItemNftMintStatus,
  Maybe,
  NftTokenDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { Loading } from '@/components/organisms/loading'
import { TokenId } from '@/components/organisms/nft'
import {
  CreateCryptoWalletConfirmModal,
  CryptoWalletConnectDrawer,
} from '@/components/organisms/nft'
import { StorageImage, useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  isConnect: boolean
  nftActive: boolean
  item: {
    id: string | null
    title: string | null
    royalty: number | null
    serialNumber: string | null
    tokenId: string | null
    chainName: string | null
  }
  nftToken: Maybe<NftTokenDetailFragment>
  issuing: boolean
}

export type Action = {
  onIssueNft: () => Promise<void>
  connectMetamask: () => Promise<void>
  connectToWeb3auth: () => Promise<void>
}

export const NftItemDetailTemplate: Template<State, Action> = ({
  state,
  onIssueNft,
  connectMetamask,
  connectToWeb3auth,
}) => {
  const { routerNames } = useRouter()
  return (
    <>
      <Head title={routerNames.OwnedItemNftItemDetail} />
      <BaseLayout
        customNavigation={
          state?.loading ? null : (
            <NftActionButtonCell
              status={state?.nftToken?.status}
              tokenDetailUri={state?.nftToken?.tokenDetailUri ?? ''}
              isConnect={state?.isConnect}
              loading={state?.issuing}
              onIssueNft={onIssueNft}
            />
          )
        }
        header={{
          back: true,
        }}
      >
        {state?.loading ? (
          <LoadingCell />
        ) : (
          <>
            <WalletConnectCell
              isConnect={state?.isConnect}
              connectMetamask={connectMetamask}
              connectToWeb3auth={connectToWeb3auth}
            />

            <Stack direction="column" sx={{ pt: 2, px: 5 }}>
              <NftItemTitleCell
                nftActive={state?.nftActive}
                title={state?.item?.title}
              />

              <NftDetailContentsCell
                nftActive={state?.nftActive}
                nftToken={state?.nftToken}
                item={state?.item}
              />
            </Stack>
          </>
        )}
      </BaseLayout>
    </>
  )
}

type WalletConnectCellProps = {
  isConnect: boolean
  connectMetamask: () => Promise<void>
  connectToWeb3auth: () => Promise<void>
}
const WalletConnectCell: Cell<WalletConnectCellProps> = ({
  isConnect,
  connectMetamask,
  connectToWeb3auth,
}) => {
  const { t } = useTranslation('nftItemDetail')
  const { showMessage, showError } = useMessage()
  const [openNftConnectDrawer, setOpenNftConnectDrawer] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [openWalletCreateConfirmModal, setOpenWalletCreateConfirmModal] =
    useState(false)

  const onConnectWeb3auth = () => {
    setConnecting(true)
    return connectToWeb3auth()
      .catch(e => {
        showError(e)
      })
      .finally(() => {
        setConnecting(false)
        setOpenWalletCreateConfirmModal(false)
        setOpenNftConnectDrawer(false)
      })
  }

  const onConnectMetamask = () => {
    setConnecting(true)
    return connectMetamask()
      .then(() => {
        showMessage(t('connected'))
      })
      .catch(e => {
        showError(e)
      })
      .finally(() => {
        setConnecting(false)
        setOpenNftConnectDrawer(false)
      })
  }

  if (isConnect) return null

  return (
    <Stack
      direction="column"
      alignItems="center"
      sx={{ px: 7, py: 5, bgcolor: 'grey.50', mb: 5 }}
    >
      <Stack direction="row" sx={{ mb: 4 }}>
        <Icons.Error color="error" sx={{ mr: 2 }} />
        <Typography variant="caption">
          {t('walletConnectDescription')}
        </Typography>
      </Stack>
      <Button
        size="small"
        sx={{ maxWidth: 88 }}
        onClick={() => setOpenNftConnectDrawer(true)}
      >
        {t('connect')}
      </Button>

      <CryptoWalletConnectDrawer
        open={openNftConnectDrawer}
        onClose={() => setOpenNftConnectDrawer(false)}
        connecting={false}
        onConnectWallet={walletType => {
          switch (walletType) {
            case 'web3auth':
              return setOpenWalletCreateConfirmModal(true)
            case 'metamask':
              return onConnectMetamask()
          }
        }}
      />
      <CreateCryptoWalletConfirmModal
        open={openWalletCreateConfirmModal}
        loading={connecting}
        onClose={() => setOpenWalletCreateConfirmModal(false)}
        onCreate={onConnectWeb3auth}
      />
    </Stack>
  )
}

type NftItemTitleCellProps = {
  nftActive: boolean
  title?: string | null
}
const NftItemTitleCell: Cell<NftItemTitleCellProps> = ({
  nftActive,
  title,
}) => {
  return (
    <Stack direction="row" alignItems="center">
      {nftActive && (
        <Icons.Nft
          filledColor
          sx={{
            mr: 2,
          }}
        />
      )}
      <Typography variant="h5">{title}</Typography>
    </Stack>
  )
}

type NftDetailContentsCellProps = {
  nftActive: boolean
  nftToken: State['nftToken']
  item: State['item']
}
const NftDetailContentsCell: Cell<NftDetailContentsCellProps> = ({
  nftActive,
  nftToken,
  item,
}) => {
  const { t } = useTranslation('nftItemDetail')

  return (
    <Box sx={{ py: 4 }}>
      <ContentBox
        title={t('serialNumber')}
        value={
          <Typography variant="subtitle1" color="text.primary">
            {item.serialNumber}
          </Typography>
        }
      />
      <ContentBox
        title={t('tokenId')}
        value={<TokenId tokenId={item.tokenId} />}
      />
      {nftActive && (
        <ContentBox
          title={t('nftOwner')}
          value={
            <Stack direction="row" alignItems="center">
              <Typography
                variant="subtitle1"
                color="text.primary"
                sx={{ maxWidth: 200, wordBreak: 'break-all' }}
              >
                {nftToken?.ownedByUserAddress}
              </Typography>
            </Stack>
          }
        />
      )}
      {nftActive && (
        <ContentBox
          title={t('nftIssuer')}
          value={
            <Stack direction="row" alignItems="center">
              <Avatar
                src={
                  nftToken?.metadata?.item?.item?.issuedArtist?.avatar
                    ?.smallUri ??
                  nftToken?.metadata?.item?.item?.issuedArtist?.avatar?.uri ??
                  ''
                }
                sx={{ mr: 2, height: 20, width: 20 }}
              />
              <Typography variant="subtitle1" color="text.primary">
                {nftToken?.metadata?.item?.item?.issuedArtist?.displayName}
              </Typography>
            </Stack>
          }
        />
      )}
      <ContentBox
        title={t('blockChain')}
        value={
          <Stack direction="row" alignItems="center">
            {item.chainName === 'polygon' && (
              <StorageImage
                target="common"
                src="images/nft/polygon.png"
                width={20}
                height={20}
              />
            )}
            <Typography variant="subtitle1" color="text.primary" sx={{ ml: 2 }}>
              {item.chainName}
            </Typography>
          </Stack>
        }
      />
      <ContentBox
        title={t('royalty')}
        value={
          <Typography variant="subtitle1" color="text.primary">
            {item.royalty}%
          </Typography>
        }
      />
      {nftActive && (
        <ContentBox
          title={t('createdAt')}
          value={
            <Typography variant="subtitle1" color="text.primary">
              {nftToken?.formattedCreatedAt}
            </Typography>
          }
        />
      )}
    </Box>
  )
}

type ContentBoxCellProps = {
  title: string
  value: ReactNode
}
const ContentBox: Cell<ContentBoxCellProps> = ({ title, value }) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        p: 3,
        border: 1,
        borderRadius: 2,
        borderColor: 'grey.100',
        mb: 3,
      }}
    >
      <Typography variant="subtitle1" color="text.secondary">
        {title}
      </Typography>
      {value}
    </Stack>
  )
}

type NftActionButtonCellProps = {
  status?: ItemNftMintStatus
  tokenDetailUri: string
  isConnect: boolean
  loading: boolean
  onIssueNft: () => Promise<void>
}
const NftActionButtonCell: Cell<NftActionButtonCellProps> = ({
  status,
  tokenDetailUri,
  isConnect,
  loading,
  onIssueNft,
}) => {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ py: 4, bgcolor: 'grey.50' }}
    >
      <NftActionButton
        status={status}
        tokenDetailUri={tokenDetailUri}
        loading={loading}
        onIssueNft={onIssueNft}
        isConnect={isConnect}
      />
    </Stack>
  )
}

const NftActionButton: Cell<NftActionButtonCellProps> = ({
  status,
  tokenDetailUri,
  isConnect,
  loading,
  onIssueNft,
}) => {
  const { t } = useTranslation('nftItemDetail')

  if (status === ItemNftMintStatus.Completed)
    return (
      <Button
        variant="outlined"
        size="large"
        /** NFT 停止に向けて、ボタンを非活性に */
        disabled={true}
        onClick={() => window.open(tokenDetailUri)}
        sx={{ width: 244 }}
      >
        {t('viewDetail')}
      </Button>
    )

  if (status === ItemNftMintStatus.Processing)
    return (
      <Button
        variant="outlined"
        size="large"
        disabled={true}
        sx={{ width: 244 }}
      >
        {t('issuing')}
      </Button>
    )

  if (status === ItemNftMintStatus.Failed)
    return (
      <Button
        variant="outlined"
        size="large"
        disabled={true}
        sx={{ width: 244 }}
      >
        {t('issueFailed')}
      </Button>
    )

  return (
    <Button
      size="large"
      sx={{ width: 244 }}
      loading={loading}
      onClick={onIssueNft}
      /** NFT 停止に向けて、ボタンを非活性に */
      disabled={true}
    >
      {t('issueNft')}
    </Button>
  )
}

const LoadingCell: Cell = () => {
  return (
    <CellFrame sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
      <Loading />
    </CellFrame>
  )
}
